import { parseCellKey } from 'components/CellAnnotationValueViewer/types';
import type {
  CellAnnotationWireEntry,
  CellAnnotationWireMap,
} from 'components/CellAnnotationValueViewer/types';
import type { WireByCellKey } from './types';

// Split a dirty cell-annotation payload into one cell_annotations bucket per
// parent type. Every annotation type (cross_reference, pre_fx_currency,
// future types) folds through the same path: `(parentId → fieldAlias →
// annotationType → entry)`. The parent serializer extracts what it needs
// (e.g. PRE_FX for FX host derivation) and persists the rest verbatim.

export interface SplitDirtyForFodSave {
  fodCellAnnotations: Record<string, CellAnnotationWireMap>;
  acquisitionCellAnnotations: Record<string, CellAnnotationWireMap>;
  saleCellAnnotations: Record<string, CellAnnotationWireMap>;
  distributionCellAnnotations: Record<string, CellAnnotationWireMap>;
}

const BUCKET_BY_PARENT: Record<string, keyof SplitDirtyForFodSave> = {
  fundownershipdetail: 'fodCellAnnotations',
  sharesacquisition: 'acquisitionCellAnnotations',
  sharessale: 'saleCellAnnotations',
  cashdistribution: 'distributionCellAnnotations',
};

const foldEntry = (
  bucket: Record<string, CellAnnotationWireMap>,
  parentId: string,
  fieldAlias: string,
  annotationType: string,
  entry: CellAnnotationWireEntry
): void => {
  // eslint-disable-next-line no-param-reassign
  const forParent = bucket[parentId] ?? (bucket[parentId] = {});
  const forField = forParent[fieldAlias] ?? (forParent[fieldAlias] = {});
  forField[annotationType as keyof typeof forField] = entry;
};

export const splitDirtyForFodSave = (payload: WireByCellKey): SplitDirtyForFodSave => {
  const result: SplitDirtyForFodSave = {
    fodCellAnnotations: {},
    acquisitionCellAnnotations: {},
    saleCellAnnotations: {},
    distributionCellAnnotations: {},
  };

  if (!payload) {
    return result;
  }

  Object.entries(payload).forEach(([cellKey, entry]) => {
    const parsed = parseCellKey(cellKey);
    if (!parsed) return;
    const bucketKey = BUCKET_BY_PARENT[parsed.parentType];
    if (!bucketKey) return;
    foldEntry(
      result[bucketKey],
      parsed.parentId,
      parsed.fieldAlias,
      parsed.annotationType,
      entry as CellAnnotationWireEntry
    );
  });

  return result;
};
